// src/pages/PrintPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useScanHandler } from "../context/ScannerContext";
import api from "../api/axios";
import "../styles/index.css";
import "./PrintPage.css";

interface FolderRow { id: number; name: string; parentId: number | null; sortOrder: number }
interface CategoryRow { id: number; name: string; folderId: number; total: number; active: number; unreviewed: number }
interface Prod {
  id: number; categoryId?: number | null; categoryName?: string | null; nameRu: string; nameKz: string; salePrice: number;
  unit: string; type: string; sku: string | null; barcode: string | null; isActive: boolean; kzReviewed: boolean; exact?: boolean;
}
interface BasketItem { p: Prod; n: number }

type Lang = "both" | "kz" | "ru";
type Tab = "price" | "labels";
type Format = "html" | "pdf";
type Layout = "3x8" | "2x5" | "1x2" | "roll";
type BarcodeSource = "sku" | "barcode";

const LAYOUTS: Record<Layout, { cols: number; rows: number; label: string }> = {
  "3x8": { cols: 3, rows: 8, label: "Ценник малый: 24 на лист A4 (70×37 мм)" },
  "2x5": { cols: 2, rows: 5, label: "Ценник средний: 10 на лист A4 (105×59 мм)" },
  "1x2": { cols: 1, rows: 2, label: "Ценник крупный: 2 на лист A4 (210×148 мм)" },
  roll: { cols: 1, rows: 1, label: "Этикетки на рулонном принтере (до 80 мм)" },
};

// частые размеры термоэтикеток, мм (ширина × высота)
const ROLL_PRESETS: [number, number][] = [[30, 20], [40, 25], [40, 30], [58, 30], [58, 40], [58, 60], [80, 40], [80, 50], [80, 60]];

const BASKET_KEY = "print.basket.v1";
const money = (v: number) => `${Number(v).toLocaleString("ru-RU")} ₸`;

async function send(path: string, body: any, format: Format, fileName: string, desk?: { kind: "labels" | "a4"; sizeMm?: { w: number; h: number } }) {
  // в настольном приложении печатаем сразу на принтер из настроек (этикетки, A4), без окна печати
  const bridge = (window as any).keyoffDesktop;
  if (format === "html" && bridge?.printHtml) {
    try {
      const res = await api.post(path, { ...body, format, autoprint: false }, { responseType: "text", transformResponse: (d) => d });
      const r = await bridge.printHtml({ html: res.data as string, kind: desk?.kind ?? "a4", sizeMm: desk?.sizeMm });
      if (!r?.ok) alert("Не напечаталось: " + (r?.reason || "ошибка принтера"));
    } catch (e: any) {
      alert("Не удалось сформировать документ" + (e?.response?.status === 400 ? ": проверьте выбор." : "."));
    }
    return;
  }
  if (format === "html") {
    const w = window.open("", "_blank");
    try {
      const res = await api.post(path, { ...body, format }, { responseType: "text", transformResponse: (d) => d });
      if (!w) { alert("Браузер заблокировал окно печати. Разрешите всплывающие окна для CRM."); return; }
      w.document.open(); w.document.write(res.data as string); w.document.close();
    } catch (e: any) {
      w?.close();
      alert("Не удалось сформировать документ" + (e?.response?.status === 400 ? ": проверьте выбор." : "."));
    }
    return;
  }
  try {
    const res = await api.post(path, { ...body, format }, { responseType: "blob" });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement("a");
    a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch {
    alert("Не удалось сформировать PDF.");
  }
}

function loadBasket(): BasketItem[] {
  try {
    const raw = JSON.parse(localStorage.getItem(BASKET_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((x) => x && x.p && x.p.id && x.n > 0) : [];
  } catch { return []; }
}

export default function PrintPage() {
  const [tab, setTab] = useState<Tab>("labels");
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [cats, setCats] = useState<CategoryRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [catSearch, setCatSearch] = useState("");
  const [busy, setBusy] = useState(false);

  // общие параметры; по умолчанию казахский + русский (закон о языках)
  const [lang, setLang] = useState<Lang>("both");
  const [onlyActive, setOnlyActive] = useState(true);
  const [logo, setLogo] = useState(true);

  // ценники
  const [layout, setLayout] = useState<Layout>("3x8");
  const [barcodeSource, setBarcodeSource] = useState<BarcodeSource>("sku");
  const [skip, setSkip] = useState(0);
  const [rollW, setRollW] = useState(58);
  const [rollH, setRollH] = useState(40);
  const [showPrice, setShowPrice] = useState(true);
  const [showName, setShowName] = useState(true);
  const [basket, setBasket] = useState<BasketItem[]>(loadBasket);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Prod[]>([]);
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null);
  const [flashId, setFlashId] = useState<number | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get("/print/catalog").then((r) => { setFolders(r.data.folders); setCats(r.data.categories); });
  }, []);

  useEffect(() => {
    try { localStorage.setItem(BASKET_KEY, JSON.stringify(basket)); } catch { /* приватный режим */ }
  }, [basket]);

  // ---------- корзина ----------
  const addProd = (p: Prod, inc = 1) => {
    setBasket((prev) => {
      const i = prev.findIndex((x) => x.p.id === p.id);
      if (i >= 0) { const n = prev.slice(); n[i] = { p: n[i].p, n: Math.min(100, n[i].n + inc) }; return n; }
      return [...prev, { p, n: inc }];
    });
    setFlashId(p.id);
    setTimeout(() => setFlashId((cur) => (cur === p.id ? null : cur)), 900);
  };
  const setCopies = (id: number, n: number) =>
    setBasket((prev) => prev.map((x) => (x.p.id === id ? { p: x.p, n: Math.max(1, Math.min(100, n || 1)) } : x)));
  const removeItem = (id: number) => setBasket((prev) => prev.filter((x) => x.p.id !== id));

  // ---------- поиск по ШК / SKU / названию ----------
  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get("/print/search", { params: { q: term, onlyActive: onlyActive ? 1 : 0 } })
        .then((r) => { if (!cancelled) setResults(r.data); })
        .catch(() => { if (!cancelled) setResults([]); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, onlyActive]);

  // Enter (сканер штрихкодов шлёт код и Enter): точное совпадение или единственный результат добавляем сразу
  const onSearchEnter = async (override?: string) => {
    const term = (override ?? q).trim();
    if (!term) return;
    try {
      const r = await api.get("/print/search", { params: { q: term, onlyActive: onlyActive ? 1 : 0 } });
      const list: Prod[] = r.data;
      const hit = list.find((x) => x.exact) || (list.length === 1 ? list[0] : null);
      if (hit) {
        addProd(hit);
        setFlash({ ok: true, text: `Добавлено: ${hit.nameRu}` });
        setQ(""); setResults([]);
        searchRef.current?.focus();
      } else {
        setResults(list);
        setFlash(list.length ? null : { ok: false, text: `Ничего не найдено по «${term}»` });
      }
    } catch {
      setFlash({ ok: false, text: "Не удалось выполнить поиск" });
    }
  };
  // код с телефона: сразу в список ценников
  useScanHandler((code) => { setQ(code); onSearchEnter(code); }, tab === "labels");

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3500);
    return () => clearTimeout(t);
  }, [flash]);

  // ---------- категории ----------
  const visibleCats = useMemo(() => cats.filter((c) => (onlyActive ? c.active : c.total) > 0), [cats, onlyActive]);
  const catsByFolder = useMemo(() => {
    const m = new Map<number, CategoryRow[]>();
    visibleCats.forEach((c) => m.set(c.folderId, [...(m.get(c.folderId) ?? []), c]));
    return m;
  }, [visibleCats]);
  const kids = useMemo(() => {
    const m = new Map<number | null, FolderRow[]>();
    folders.forEach((f) => m.set(f.parentId, [...(m.get(f.parentId) ?? []), f]));
    return m;
  }, [folders]);

  const catIdsUnder = (f: FolderRow): number[] => [
    ...(catsByFolder.get(f.id) ?? []).map((c) => c.id),
    ...(kids.get(f.id) ?? []).flatMap(catIdsUnder),
  ];
  const toggle = (ids: number[], on: boolean) =>
    setSelected((prev) => { const n = new Set(prev); ids.forEach((i) => (on ? n.add(i) : n.delete(i))); return n; });

  const cq = catSearch.trim().toLowerCase();
  const matches = (c: CategoryRow) => !cq || c.name.toLowerCase().includes(cq);

  const renderFolder = (f: FolderRow, level: number): React.ReactNode => {
    const ids = catIdsUnder(f);
    if (!ids.length) return null;
    const own = (catsByFolder.get(f.id) ?? []).filter(matches);
    const sub = (kids.get(f.id) ?? []).map((k) => renderFolder(k, level + 1)).filter(Boolean);
    if (!own.length && !sub.length) return null;
    const on = ids.filter((i) => selected.has(i)).length;
    return (
      <div key={f.id} style={{ marginLeft: level ? 14 : 0, marginBottom: 6 }}>
        <label className="pp-folder">
          <input
            type="checkbox"
            checked={on === ids.length}
            ref={(el) => { if (el) el.indeterminate = on > 0 && on < ids.length; }}
            onChange={(e) => toggle(ids, e.target.checked)}
          />
          {f.name}
        </label>
        {own.map((c) => (
          <label key={c.id} className="pp-cat">
            <input type="checkbox" checked={selected.has(c.id)} onChange={(e) => toggle([c.id], e.target.checked)} />
            <span>{c.name}</span>
            <em>({onlyActive ? c.active : c.total})</em>
          </label>
        ))}
        {sub}
      </div>
    );
  };

  const roots = (kids.get(null) ?? []).filter((f) => !/al-?style/i.test(f.name));
  const selectedIds = useMemo(() => Array.from(selected).filter((id) => visibleCats.some((c) => c.id === id)), [selected, visibleCats]);
  const selectedCount = useMemo(
    () => visibleCats.filter((c) => selected.has(c.id)).reduce((s, c) => s + (onlyActive ? c.active : c.total), 0),
    [visibleCats, selected, onlyActive],
  );

  // добавить в список все товары выбранных категорий
  const addSelectedCategories = async () => {
    if (!selectedIds.length) return;
    try {
      const r = await api.get("/print/products", { params: { categoryIds: selectedIds.join(","), onlyActive: onlyActive ? 1 : 0 } });
      const list: Prod[] = r.data;
      setBasket((prev) => {
        const have = new Set(prev.map((x) => x.p.id));
        return [...prev, ...list.filter((p) => !have.has(p.id)).map((p) => ({ p, n: 1 }))];
      });
      setFlash({ ok: true, text: `Добавлено позиций: ${list.length}` });
    } catch {
      setFlash({ ok: false, text: "Не удалось загрузить товары категорий" });
    }
  };

  // ---------- итоги и печать ----------
  const totalLabels = basket.reduce((s, x) => s + x.n, 0);
  const cfg = LAYOUTS[layout];
  const isRoll = layout === "roll";
  const perPage = cfg.cols * cfg.rows;
  const safeSkip = isRoll ? 0 : Math.min(skip, perPage - 1);
  const sheets = isRoll ? totalLabels : Math.max(1, Math.ceil((totalLabels + safeSkip) / perPage));
  const clampMm = (v: number, a: number, b: number) => Math.min(Math.max(Math.round(v) || a, a), b);
  const rw = clampMm(rollW, 20, 80);
  const rh = clampMm(rollH, 15, 200);
  const unreviewedCount = tab === "price"
    ? visibleCats.filter((c) => selected.has(c.id)).reduce((s, c) => s + (c.unreviewed ?? 0), 0)
    : basket.filter((x) => !x.p.kzReviewed).length;
  const noEan = barcodeSource === "barcode" ? basket.filter((x) => !(x.p.barcode ?? "").trim()).length : 0;

  const run = async (format: Format) => {
    setBusy(true);
    try {
      if (tab === "price") {
        await send("/print/price-list", { categoryIds: selectedIds, lang, onlyActive, logo }, format, "price-list.pdf");
      } else {
        await send(
          "/print/labels",
          {
            items: basket.map((x) => ({ productId: x.p.id, copies: x.n })), layout, logo, lang, barcodeSource, skip: safeSkip,
            ...(isRoll ? { rollWidth: rw, rollHeight: rh, showPrice, showName } : {}),
          },
          format, "price-tags.pdf",
          isRoll ? { kind: "labels", sizeMm: { w: rw, h: rh } } : { kind: "a4" },
        );
      }
    } finally { setBusy(false); }
  };
  const canRun = tab === "price" ? selectedIds.length > 0 : totalLabels > 0;

  // ---------- общие блоки ----------
  const langBlock = (
    <>
      <div className="pp-field">
        <span>Язык / Тіл</span>
        <select className="pp-select" value={lang} onChange={(e) => setLang(e.target.value as Lang)}>
          <option value="both">Қазақша + Русский (рекомендуется)</option>
          <option value="kz">Қазақша</option>
          <option value="ru">Русский</option>
        </select>
      </div>
      {lang === "ru" && (
        <p className="pp-warn">Реклама, прайсы и ценники по закону о языках должны быть на государственном (казахском) языке; русский добавляется по желанию.</p>
      )}
      {lang !== "ru" && unreviewedCount > 0 && (
        <p className="pp-warn">
          ⚠ Казахский перевод {unreviewedCount} выбранных позиций пока черновой (не вычитан носителем языка). Проверьте его и отметьте
          «Перевод вычитан» в карточке товара, прежде чем использовать в рекламе.
        </p>
      )}
    </>
  );

  const sheetCells = Array.from({ length: perPage }, (_, i) => {
    const cls = i < safeSkip ? "skip" : i < safeSkip + totalLabels ? "fill" : "";
    return <button key={i} className={`pp-cell ${cls}`} title={`Начать с ячейки ${i + 1}`} onClick={() => setSkip(i)} />;
  });

  return (
    <div className="pp-page">
      <div className="pp-top">
        <h2>Печать</h2>
        <div className="pp-seg">
          <button className={tab === "labels" ? "on" : ""} onClick={() => setTab("labels")}>🏷 Ценники</button>
          <button className={tab === "price" ? "on" : ""} onClick={() => setTab("price")}>🧾 Прайс-лист</button>
        </div>
      </div>

      {tab === "labels" ? (
        <div className="pp-grid">
          {/* ЛЕВО: поиск и добавление */}
          <div className="pp-col">
            <div className="pp-card">
              <h3>Найти товар</h3>
              <div className="pp-search">
                <span className="pp-search-ico">🔍</span>
                <input
                  ref={searchRef}
                  className="pp-input"
                  autoFocus
                  placeholder="Штрихкод, SKU или название"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSearchEnter(); } }}
                />
              </div>
              <div className="pp-hint">
                Сканер штрихкодов вводит код и нажимает Enter: товар сразу попадёт в список, повторный скан добавит копию. Название можно вводить частями.
              </div>
              {flash && <div className={`pp-flash ${flash.ok ? "" : "err"}`}>{flash.text}</div>}
              {q.trim() && (
                <div className="pp-results">
                  {results.length === 0 ? (
                    <div className="pp-res" style={{ cursor: "default" }}><span className="pp-res-sub">Нет совпадений</span></div>
                  ) : results.map((p) => (
                    <div key={p.id} className={`pp-res ${p.exact ? "exact" : ""}`} onClick={() => addProd(p)}>
                      <div className="pp-res-main">
                        <div className="pp-res-name">{p.nameRu}{!p.isActive && <span className="pp-inactive">неактивен</span>}</div>
                        <div className="pp-res-sub">
                          {[p.sku && `SKU ${p.sku}`, p.barcode && `ШК ${p.barcode}`, p.categoryName].filter(Boolean).join(" · ") || "без артикула"}
                        </div>
                      </div>
                      <div className="pp-res-price">{money(p.salePrice)}</div>
                      <button className="pp-add" title="Добавить в список" onClick={(e) => { e.stopPropagation(); addProd(p); }}>+</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pp-card">
              <details className="pp-details">
                <summary>Добавить из категорий</summary>
                <input className="pp-input" style={{ marginTop: 10 }} placeholder="Поиск категории…" value={catSearch} onChange={(e) => setCatSearch(e.target.value)} />
                <div className="pp-row">
                  <button className="pp-btn small" onClick={() => toggle(visibleCats.map((c) => c.id), true)}>Выбрать все</button>
                  <button className="pp-btn small" onClick={() => setSelected(new Set())}>Снять выбор</button>
                </div>
                <div className="pp-tree">{roots.map((f) => renderFolder(f, 0))}</div>
                <div className="pp-row">
                  <button className="pp-btn primary" disabled={!selectedIds.length} onClick={addSelectedCategories}>
                    + Добавить в список{selectedIds.length ? ` (${selectedCount})` : ""}
                  </button>
                </div>
              </details>
            </div>
          </div>

          {/* ПРАВО: список, настройки, лист */}
          <div className="pp-col">
            <div className="pp-card">
              <h3>
                Список для печати <span className="pp-count">{basket.length}</span>
                {basket.length > 0 && (
                  <button className="pp-btn small ghost" style={{ marginLeft: "auto", textTransform: "none", letterSpacing: 0 }} onClick={() => setBasket([])}>
                    Очистить
                  </button>
                )}
              </h3>
              {basket.length === 0 ? (
                <div className="pp-empty"><b>🏷</b>Список пуст. Отсканируйте штрихкод или найдите товар через поиск.</div>
              ) : (
                <div className="pp-list">
                  {basket.map(({ p, n }) => (
                    <div key={p.id} className={`pp-item ${flashId === p.id ? "flash" : ""}`}>
                      <div className="pp-item-main">
                        <div className="pp-item-name">{p.nameRu}</div>
                        <div className="pp-item-sub">{[p.sku && `SKU ${p.sku}`, p.barcode && `ШК ${p.barcode}`].filter(Boolean).join(" · ") || "без артикула"}</div>
                      </div>
                      <div className="pp-item-price">{money(p.salePrice)}</div>
                      <div className="pp-step">
                        <button onClick={() => setCopies(p.id, n - 1)} aria-label="Меньше">−</button>
                        <input type="number" min={1} max={100} value={n} onChange={(e) => setCopies(p.id, Number(e.target.value))} />
                        <button onClick={() => setCopies(p.id, n + 1)} aria-label="Больше">+</button>
                      </div>
                      <button className="pp-x" onClick={() => removeItem(p.id)} title="Убрать">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pp-card">
              <h3>Параметры ценников</h3>
              <div className="pp-two">
                <div className="pp-field">
                  <span>Вид печати</span>
                  <select className="pp-select" value={layout} onChange={(e) => { setLayout(e.target.value as Layout); setSkip(0); }}>
                    {(Object.keys(LAYOUTS) as Layout[]).map((k) => <option key={k} value={k}>{LAYOUTS[k].label}</option>)}
                  </select>
                </div>
                <div className="pp-field">
                  <span>Штрихкод на ценнике</span>
                  <select className="pp-select" value={barcodeSource} onChange={(e) => setBarcodeSource(e.target.value as BarcodeSource)}>
                    <option value="sku">По SKU (артикулу)</option>
                    <option value="barcode">По штрихкоду товара (EAN)</option>
                  </select>
                </div>
              </div>
              {isRoll && (
                <>
                  <div className="pp-field">
                    <span>Размер этикетки, мм (ширина × высота)</span>
                    <div className="pp-two">
                      <select className="pp-select" value={`${rw}x${rh}`} onChange={(e) => { const [a, b] = e.target.value.split("x").map(Number); setRollW(a); setRollH(b); }}>
                        {!ROLL_PRESETS.some(([a, b]) => a === rw && b === rh) && <option value={`${rw}x${rh}`}>Свой: {rw}×{rh}</option>}
                        {ROLL_PRESETS.map(([a, b]) => <option key={`${a}x${b}`} value={`${a}x${b}`}>{a} × {b}</option>)}
                      </select>
                      <div className="pp-two">
                        <input className="pp-input" type="number" min={20} max={80} value={rollW} onChange={(e) => setRollW(Number(e.target.value))} title="Ширина, мм (20–80)" />
                        <input className="pp-input" type="number" min={15} max={200} value={rollH} onChange={(e) => setRollH(Number(e.target.value))} title="Высота, мм (15–200)" />
                      </div>
                    </div>
                  </div>
                  <label className="pp-check"><input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} />Название товара</label>
                  <label className="pp-check"><input type="checkbox" checked={showPrice} onChange={(e) => setShowPrice(e.target.checked)} />Цена (если снять, получится этикетка только со штрихкодом)</label>
                  {rw <= 40 && basket.some((x) => (x.p.sku ?? "").length > 10) && (
                    <p className="pp-warn">На узкой этикетке длинные артикулы дают плотный штрихкод, который хуже сканируется. Берите ширину от 58 мм или штрихкод товара (EAN).</p>
                  )}
                </>
              )}
              {noEan > 0 && <p className="pp-warn">У {noEan} позиций нет штрихкода в карточке: для них штрихкод будет по SKU.</p>}
              {langBlock}
              <label className="pp-check"><input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />Искать только среди активных</label>
              <label className="pp-check"><input type="checkbox" checked={logo} onChange={(e) => setLogo(e.target.checked)} />Логотип организации</label>
            </div>

            {isRoll ? (
              <div className="pp-card">
                <h3>Этикетка {rw}×{rh} мм</h3>
                <div className="pp-sheet-wrap">
                  <div className="pp-roll" style={{ width: Math.min(220, rw * 3.2), aspectRatio: `${rw} / ${rh}` }}>
                    {showName && <div className="pp-roll-name">{basket[0]?.p.nameRu ?? "Название товара"}</div>}
                    {showPrice && <div className="pp-roll-price">{(basket[0]?.p.salePrice ?? 12900).toLocaleString("ru-RU")} <small>тг</small></div>}
                    <div className="pp-roll-bc" />
                    <div className="pp-roll-code">{basket[0]?.p.sku || "ARTICLE-001"}</div>
                  </div>
                  <div className="pp-sheet-note">
                    <div><b>{totalLabels}</b> этикеток, каждая на отдельной «странице» {rw}×{rh} мм.</div>
                    <div>В окне печати выберите принтер этикеток: размер бумаги подставится сам. Если принтер не подхватил размер, задайте его в настройках драйвера, поля 0.</div>
                    <div>Для печати без диалога запускайте Chrome с ключом <b>--kiosk-printing</b> (принтер этикеток по умолчанию).</div>
                  </div>
                </div>
              </div>
            ) : (
            <div className="pp-card">
              <h3>Лист A4</h3>
              <div className="pp-sheet-wrap">
                <div className="pp-sheet" style={{ gridTemplateColumns: `repeat(${cfg.cols}, 1fr)`, gridTemplateRows: `repeat(${cfg.rows}, 1fr)` }}>
                  {sheetCells}
                </div>
                <div className="pp-sheet-note">
                  <div><b>{totalLabels}</b> ценн. на <b>{sheets}</b> л.</div>
                  <div>Начать печать с ячейки <b>{safeSkip + 1}</b>: нажмите на нужную ячейку схемы, чтобы доиспользовать начатый лист. Серые ячейки пропускаются.</div>
                  {safeSkip > 0 && <button className="pp-btn small" style={{ marginTop: 8 }} onClick={() => setSkip(0)}>С начала листа</button>}
                </div>
              </div>
            </div>
            )}

            <div className="pp-actions">
              <button className="pp-btn primary" disabled={!canRun || busy} onClick={() => run("html")}>🖨 Печать</button>
              <button className="pp-btn" disabled={!canRun || busy} onClick={() => run("pdf")}>⬇ Скачать PDF</button>
              <span className="pp-sum">{busy ? "Готовлю…" : isRoll ? `Этикеток: ${totalLabels}` : `Ценников: ${totalLabels}, листов: ${sheets}`}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="pp-grid">
          <div className="pp-col">
            <div className="pp-card">
              <h3>Категории <span className="pp-count">{selectedIds.length}</span></h3>
              <input className="pp-input" placeholder="Поиск категории…" value={catSearch} onChange={(e) => setCatSearch(e.target.value)} />
              <div className="pp-row">
                <button className="pp-btn small" onClick={() => toggle(visibleCats.map((c) => c.id), true)}>Выбрать все</button>
                <button className="pp-btn small" onClick={() => setSelected(new Set())}>Снять выбор</button>
              </div>
              <div className="pp-tree" style={{ marginTop: 0 }}>{roots.map((f) => renderFolder(f, 0))}</div>
            </div>
          </div>
          <div className="pp-col">
            <div className="pp-card">
              <h3>Параметры прайс-листа</h3>
              {langBlock}
              <label className="pp-check"><input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />Только активные позиции</label>
              <label className="pp-check"><input type="checkbox" checked={logo} onChange={(e) => setLogo(e.target.checked)} />Логотип организации</label>
            </div>
            <div className="pp-actions">
              <button className="pp-btn primary" disabled={!canRun || busy} onClick={() => run("html")}>🖨 Печать</button>
              <button className="pp-btn" disabled={!canRun || busy} onClick={() => run("pdf")}>⬇ Скачать PDF</button>
              <span className="pp-sum">{busy ? "Готовлю…" : `Категорий: ${selectedIds.length}, позиций: ${selectedCount}`}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
